import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { api } from '../../services/api';
import { 
  ArrowLeft,
  Mail,
  Phone,
  Stethoscope,
  Briefcase,
  Trash2,
  AlertCircle,
  AlertTriangle,
  Bell,
} from 'lucide-react';
import { Button } from '../../components/ui';

export function UserDetailPage() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();

  const [targetUser, setTargetUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [updatingNotifications, setUpdatingNotifications] = useState(false);

  const isSuperAdmin = currentUser?.acl_care?.acl_care_superadmin === true;

  // Fetch user data
  useEffect(() => {
    async function fetchUserData() {
      try {
        setLoading(true);
        setError(null);

        const usersResult = isSuperAdmin
          ? await api.getAllACLUsers()
          : await api.getAllUsers(currentUser.organization_id);

        const users = usersResult.users || usersResult || [];
        const foundUser = users.find(u => u.id === userId);

        if (!foundUser) {
          setError('User not found');
          return;
        }

        setTargetUser(foundUser);
      } catch (err) {
        console.error('[UserDetailPage] Error fetching data:', err);
        setError('Failed to load user data');
      } finally {
        setLoading(false);
      }
    }

    fetchUserData();
  }, [userId, isSuperAdmin, currentUser.organization_id]);

  const handleRemoveUser = async () => {
    try {
      setRemoving(true);
      
      // Call API to remove user from organization
      await api.removeUserFromOrganization(userId);
      
      // Navigate back to users page
      navigate('/users');
    } catch (err) {
      console.error('[UserDetailPage] Error removing user:', err);
      setError('Failed to remove user. Please try again.');
      setShowRemoveConfirm(false);
    } finally {
      setRemoving(false);
    }
  };

  const handleNotificationsToggle = async () => {
    if (updatingNotifications || !targetUser) return;
    
    setUpdatingNotifications(true);
    try {
      const currentValue = targetUser.acl_care?.receives_test_out_notifications || false;
      const newValue = !currentValue;
      
      // Merge with existing acl_care properties
      const updatedAclCare = {
        ...targetUser.acl_care,
        receives_test_out_notifications: newValue,
      };
      
      await api.updateUser(userId, { acl_care: updatedAclCare });
      
      // Update local state
      setTargetUser(prev => ({
        ...prev,
        acl_care: updatedAclCare,
      }));
    } catch (err) {
      console.error('Error updating notification preference:', err);
      setError('Failed to update notification preference. Please try again.');
    } finally {
      setUpdatingNotifications(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-2xl mx-auto p-4 flex items-center justify-center min-h-[50vh]">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error || !targetUser) {
    return (
      <div className="max-w-2xl mx-auto p-4">
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-3" />
          <h2 className="text-lg font-semibold text-red-800 mb-2">Error</h2>
          <p className="text-red-600 mb-4">{error || 'User not found'}</p>
          <Button onClick={() => navigate('/users')}>
            Back to Team
          </Button>
        </div>
      </div>
    );
  }

  const fullName = `${targetUser.first_name || ''} ${targetUser.last_name || ''}`.trim() || 'Unknown';
  const initials = `${(targetUser.first_name || '?')[0]}${(targetUser.last_name || '?')[0]}`.toUpperCase();
  const isProvider = targetUser.is_provider === true;
  const userType = isProvider ? 'Provider' : 'Staff';
  const receivesNotifications = targetUser.acl_care?.receives_test_out_notifications || false;

  return (
    <div className="max-w-2xl mx-auto p-4 pb-24 lg:pb-8">
      {/* Header */}
      <div className="flex items-center mb-6">
        <button
          onClick={() => navigate('/users')}
          className="p-2 -ml-2 mr-2 rounded-full hover:bg-gray-100"
        >
          <ArrowLeft className="h-5 w-5 text-gray-600" />
        </button>
        <h1 className="text-2xl font-bold text-gray-900">{userType} Details</h1>
      </div>

      {/* Profile Card */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-4">
        <div className="flex items-start">
          {targetUser.profile_photo ? (
            <img 
              src={targetUser.profile_photo} 
              alt={fullName}
              className="h-20 w-20 rounded-full object-cover mr-4"
            />
          ) : (
            <div className="h-20 w-20 rounded-full bg-primary/10 flex items-center justify-center mr-4">
              <span className="text-2xl text-primary font-semibold">{initials}</span>
            </div>
          )}
          
          <div className="flex-1">
            <div className="flex items-center mb-1">
              <h2 className="text-xl font-bold text-gray-900 mr-2">{fullName}</h2>
              <span className={`
                text-xs px-2 py-0.5 rounded-full
                ${isProvider 
                  ? 'bg-blue-100 text-blue-700' 
                  : 'bg-gray-100 text-gray-700'
                }
              `}>
                {userType}
              </span>
            </div>
            
            <div className="mt-2 space-y-1">
              {targetUser.email && (
                <div className="flex items-center text-sm text-gray-600">
                  <Mail className="h-4 w-4 mr-2 text-gray-400" />
                  <a href={`mailto:${targetUser.email}`} className="hover:text-primary">
                    {targetUser.email}
                  </a>
                </div>
              )}
              {targetUser.phone && (
                <div className="flex items-center text-sm text-gray-600">
                  <Phone className="h-4 w-4 mr-2 text-gray-400" />
                  <a href={`tel:${targetUser.phone}`} className="hover:text-primary">
                    {targetUser.phone}
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* User Type Info - not necessary anymore, removing
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-4">
        <div className="flex items-center">
          {isProvider ? (
            <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center mr-3">
              <Stethoscope className="h-5 w-5 text-blue-600" />
            </div>
          ) : (
            <div className="h-10 w-10 rounded-full bg-gray-100 flex items-center justify-center mr-3">
              <Briefcase className="h-5 w-5 text-gray-600" />
            </div>
          )}
          <div>
            <p className="font-medium text-gray-900">{userType}</p>
            <p className="text-sm text-gray-500">
              {isProvider 
                ? 'Treats patients and provides medical care' 
                : 'Organization staff member'
              }
            </p>
          </div>
        </div>
      </div>
       */}

      {/* Notification Settings - Only for Providers */}
      {isProvider && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-4">
          <h3 className="font-semibold text-gray-900 mb-3 flex items-center">
            <Bell className="h-5 w-5 mr-2 text-primary" />
            Notification Settings
          </h3>
          
          <div className="flex items-start gap-3">
            <div className="flex items-center h-6">
              <button
                type="button"
                role="switch"
                aria-checked={receivesNotifications}
                disabled={updatingNotifications}
                onClick={handleNotificationsToggle}
                className={`
                  relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent 
                  transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2
                  ${receivesNotifications ? 'bg-primary' : 'bg-gray-200'}
                  ${updatingNotifications ? 'opacity-50 cursor-not-allowed' : ''}
                `}
              >
                <span
                  className={`
                    pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 
                    transition duration-200 ease-in-out
                    ${receivesNotifications ? 'translate-x-5' : 'translate-x-0'}
                  `}
                />
              </button>
            </div>
            <div className="flex-1">
              <span className="text-sm text-gray-900 font-medium block mb-1">
                Test Out Notifications
              </span>
              <span className="text-sm text-gray-600">
                When enabled, this provider will receive notifications when clients complete test outs.
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Remove User */}
      <div className="bg-red-50 rounded-xl border border-red-200 p-4">
        <div className="flex items-start">
          <AlertTriangle className="h-5 w-5 text-red-500 mr-3 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <h3 className="font-semibold text-red-800 mb-1">Remove User</h3>
            <p className="text-sm text-red-600 mb-3">
              This will remove {fullName} from the organization. 
              They will no longer have access to the system.
            </p>
            <Button
              variant="outline"
              onClick={() => setShowRemoveConfirm(true)}
              className="border-red-300 text-red-600 hover:bg-red-100 hover:border-red-400"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Remove {userType}
            </Button>
          </div>
        </div>
      </div>

      {/* Remove Confirmation Modal */}
      {showRemoveConfirm && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="fixed inset-0 bg-black/50" onClick={() => setShowRemoveConfirm(false)} />
          
          <div className="flex min-h-full items-end justify-center p-4 sm:items-center">
            <div className="relative bg-white rounded-t-xl sm:rounded-xl shadow-xl w-full max-w-md p-6">
              <div className="flex items-center mb-4">
                <div className="h-10 w-10 rounded-full bg-red-100 flex items-center justify-center mr-3">
                  <AlertTriangle className="h-5 w-5 text-red-600" />
                </div>
                <h2 className="text-lg font-semibold text-gray-900">
                  Confirm Removal
                </h2>
              </div>
              
              <p className="text-gray-600 mb-6">
                Are you sure you want to remove <strong>{fullName}</strong> from the organization? 
                This action cannot be undone.
              </p>

              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => setShowRemoveConfirm(false)}
                  fullWidth
                  disabled={removing}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleRemoveUser}
                  fullWidth
                  disabled={removing}
                  className="bg-red-600 hover:bg-red-700 focus:ring-red-500"
                >
                  {removing ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                      Removing...
                    </>
                  ) : (
                    <>
                      <Trash2 className="h-4 w-4 mr-2" />
                      Remove
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}